/**
 * Utils to manipulate the graphql request programatically
 * https://github.com/domasx2/graphql-combine-query
 */

import { Kind, SelectionSetNode } from 'graphql';
import {
    ArgumentNode,
    DefinitionNode,
    DirectiveNode,
    DocumentNode,
    FieldNode,
    OperationDefinitionNode,
    SelectionNode,
    ValueNode,
    VariableDefinitionNode,
} from 'graphql/language';

type OperationVariables = Record<string, any>;
type RenameFn = (name: string) => string;
type RenameFnWithIndex = (name: string, index: number) => string;
// These query options will improve the resultant combined query by removing certain fields from resultant query document
type QueryOptions = {
    // array of top level fields that will be present in intermediate queries, all other fields will get removed
    whiteListedTopLevelFieldsOnSelectionSet?: string[];
    // remove fragment spreads from intermediate queries
    removeFragmentSpreads?: boolean;
    // remove inline fragments from intermediate queries
    removeInlineFragments?: boolean;
    // remove multiple client side params from combined query parameter list,
    // as they might be required only in last query due to above optimizations.
    // By default we will keep the last instance of a client side parameter.
    clientSideParams?: string[];
};

const defaultRenameFn: RenameFnWithIndex = (name, index) => `${name}_${index}`;
const emptyDoc: DocumentNode = {
    kind: Kind.DOCUMENT,
    definitions: [],
};

interface NewCombinedQueryBuilder {
    operationName: string;
    add: <TData = any, TVariables = OperationVariables>(
        document: DocumentNode,
        variables?: TVariables,
    ) => CombinedQueryBuilder<TData, TVariables>;
    addN: <TVariables = OperationVariables>(
        document: DocumentNode,
        variables: TVariables[],
        queryOptions: QueryOptions,
        variableRenameFn?: RenameFnWithIndex,
        fieldRenameFn?: RenameFnWithIndex,
    ) => CombinedQueryBuilder<object, object>;
}

export interface CombinedQueryBuilder<
    TData = any,
    TVariables extends OperationVariables = object
> {
    document: DocumentNode;
    variables?: TVariables;
    add: <TDataAdd = any, TVariablesAdd = OperationVariables>(
        document: DocumentNode,
        variables?: TVariablesAdd,
        queryOptions?: QueryOptions,
    ) => CombinedQueryBuilder<TData & TDataAdd, TVariables & TVariablesAdd>;
    addN: <TVariablesAdd = OperationVariables>(
        document: DocumentNode,
        variables: TVariablesAdd[],
        queryOptions: QueryOptions,
        variableRenameFn?: RenameFnWithIndex,
        fieldRenameFn?: RenameFnWithIndex,
    ) => CombinedQueryBuilder<TData, TVariables>;
}

function renameValue(node: ValueNode, renameFn: RenameFn): ValueNode {
    if (node.kind === 'Variable') {
        return {
            ...node,
            name: {
                ...node.name,
                value: renameFn(node.name.value),
            },
        };
    }
    if (node.kind === 'ObjectValue') {
        return {
            ...node,
            fields: node.fields.map(field => ({
                ...field,
                value: renameValue(field.value, renameFn),
            })),
        };
    }
    if (node.kind === 'ListValue') {
        return {
            ...node,
            values: node.values.map(value => renameValue(value, renameFn)),
        };
    }

    return node;
}

function renameArgument(node: ArgumentNode, renameFn: RenameFn): ArgumentNode {
    return {
        ...node,
        value: renameValue(node.value, renameFn),
    };
}

function renameDirectiveArguments(
    node: DirectiveNode,
    renameFn: RenameFn,
): DirectiveNode {
    return {
        ...node,
        arguments: node.arguments?.map(arg => renameArgument(arg, renameFn)),
    };
}

function renameVariableDefinition(
    node: VariableDefinitionNode,
    renameFn: RenameFn,
): VariableDefinitionNode {
    return {
        ...node,
        variable: {
            ...node.variable,
            name: {
                ...node.variable.name,
                value: renameFn(node.variable.name.value),
            },
        },
        directives: (node.directives as any)?.map((dir: any) =>
            renameDirectiveArguments(dir, renameFn),
        ),
    };
}

function renameSelectionSetArguments(
    selectionSet: SelectionSetNode,
    renameFn: (name: string) => string,
): SelectionSetNode {
    return {
        ...selectionSet,
        selections: selectionSet.selections.map(sel => {
            switch (sel.kind) {
                case 'Field':
                    return {
                        ...sel,
                        arguments: sel.arguments?.map(arg =>
                            renameArgument(arg, renameFn),
                        ),
                        selectionSet: sel.selectionSet
                            ? renameSelectionSetArguments(
                                  sel.selectionSet,
                                  renameFn,
                              )
                            : undefined,
                    };
                case 'FragmentSpread':
                    return {
                        ...sel,
                        directives: sel.directives?.map(dir =>
                            renameDirectiveArguments(dir, renameFn),
                        ),
                    };
                case 'InlineFragment':
                    return {
                        ...sel,
                        directives: sel.directives?.map(dir =>
                            renameDirectiveArguments(dir, renameFn),
                        ),
                        selectionSet: renameSelectionSetArguments(
                            sel.selectionSet,
                            renameFn,
                        ),
                    };
                default:
                    return sel;
            }
        }),
    };
}

function renameVariablesAndTopLevelFieldsOnOpDef(
    op: OperationDefinitionNode,
    variableRenameFn: RenameFn,
    fieldRenameFn: RenameFn,
): OperationDefinitionNode {
    return {
        ...op,
        variableDefinitions: op.variableDefinitions?.map(vardef =>
            renameVariableDefinition(vardef, variableRenameFn),
        ),
        directives: op.directives?.map(dir =>
            renameDirectiveArguments(dir, variableRenameFn),
        ),
        selectionSet: renameSelectionSetArguments(
            {
                ...op.selectionSet,
                selections: op.selectionSet.selections.map(sel => {
                    switch (sel.kind) {
                        case 'Field':
                            return {
                                ...sel,
                                alias: {
                                    ...sel.name,
                                    value: fieldRenameFn(
                                        sel.alias?.value ?? sel.name.value,
                                    ),
                                },
                            };
                        default:
                            return sel;
                    }
                }),
            },
            variableRenameFn,
        ),
    };
}

function renameVariablesAndTopLevelFields(
    doc: DocumentNode,
    variableRenameFn: RenameFn,
    fieldRenameFn: RenameFn,
): DocumentNode {
    return {
        ...doc,
        definitions: [
            ...doc.definitions.filter(
                def => def.kind !== 'OperationDefinition',
            ),
            ...doc.definitions
                .filter(
                    (def: DefinitionNode): def is OperationDefinitionNode =>
                        def.kind === 'OperationDefinition',
                )
                .map(opDef => {
                    return renameVariablesAndTopLevelFieldsOnOpDef(
                        opDef,
                        variableRenameFn,
                        fieldRenameFn,
                    );
                }),
        ],
    };
}

function renameVariables(
    variables: Record<string, any>,
    renameFn: RenameFn,
): Record<string, any> {
    return Object.keys(variables).reduce((vars, key) => {
        return {
            ...vars,
            [renameFn(key)]: variables[key],
        };
    }, {});
}

function removeTopLevelFieldsOnSelectionSet(
    selections: SelectionNode[],
    whiteListedFields: string[],
) {
    if (!whiteListedFields || !whiteListedFields.length) {
        return selections;
    }
    return selections?.filter(def => {
        if (def.kind === 'Field') {
            return whiteListedFields.includes(def?.name?.value);
        }
        return true;
    });
}

function removeFragmentSpreadsOnSelectionSet(selections: SelectionNode[]) {
    return selections?.filter(def => {
        if (def.kind === 'FragmentSpread') {
            return false;
        }
        if (def.kind === 'Field' && def?.selectionSet) {
            const result = removeFragmentSpreadsOnSelectionSet(
                def.selectionSet?.selections as SelectionNode[],
            );
            if (result && result.length) {
                // eslint-disable-next-line no-param-reassign
                def.selectionSet.selections = result;
                return true;
            }
            return false;
        }
        return true;
    });
}

function removeInlineFragmentsOnSelectionSet(selections: SelectionNode[]) {
    return selections?.filter(def => {
        if (def.kind === 'InlineFragment') {
            return false;
        }
        if (def.kind === 'Field' && def?.selectionSet) {
            const result = removeInlineFragmentsOnSelectionSet(
                def.selectionSet?.selections as SelectionNode[],
            );
            if (result && result.length) {
                // eslint-disable-next-line no-param-reassign
                def.selectionSet.selections = result;
                return true;
            }
            return false;
        }
        return true;
    });
}

function removeClientSideParamFromVariableDefinitions(
    variableDefinitions: VariableDefinitionNode[],
    clientSideParams: string[],
) {
    return variableDefinitions.filter(variableDefinition => {
        if (variableDefinition.kind === 'VariableDefinition') {
            const variableName = variableDefinition?.variable?.name?.value;
            return !clientSideParams.find(clientSideParam =>
                variableName.includes(clientSideParam),
            );
        }
        return true;
    });
}

class CombinedQueryBuilderImpl<TData = any, TVariables = OperationVariables>
    implements CombinedQueryBuilder<TData, TVariables> {
    document: DocumentNode;

    variables?: TVariables;

    constructor(
        private operationName: string,
        document: DocumentNode,
        variables?: TVariables,
    ) {
        this.document = document;
        this.variables = variables;
    }

    add<TDataAdd = any, TVariablesAdd = OperationVariables>(
        document: DocumentNode,
        variables?: TVariablesAdd,
        queryOptions?: QueryOptions,
    ): CombinedQueryBuilder<TData & TDataAdd, TVariables & TVariablesAdd> {
        const opDefs = this.document.definitions
            .concat(document.definitions)
            .filter(
                (def: DefinitionNode): def is OperationDefinitionNode =>
                    def.kind === 'OperationDefinition',
            );
        if (!opDefs.length) {
            throw new Error(
                'Expected at least one OperationDefinition, but found none.',
            );
        }

        // do some basic validation
        opDefs.forEach(def => {
            const otherOpDefs = opDefs.filter(_def => _def !== def);

            const opDefSelections = def.selectionSet.selections;
            opDefSelections.forEach(opDefSelection => {
                if (
                    opDefSelection?.kind === 'Field' &&
                    opDefSelection?.selectionSet?.selections
                ) {
                    if (queryOptions?.removeFragmentSpreads) {
                        // eslint-disable-next-line no-param-reassign
                        opDefSelection.selectionSet.selections = removeFragmentSpreadsOnSelectionSet(
                            opDefSelection?.selectionSet
                                .selections as SelectionNode[],
                        );
                    }

                    if (queryOptions?.removeInlineFragments) {
                        // eslint-disable-next-line no-param-reassign
                        opDefSelection.selectionSet.selections = removeInlineFragmentsOnSelectionSet(
                            opDefSelection?.selectionSet
                                .selections as SelectionNode[],
                        );
                    }

                    if (
                        queryOptions?.whiteListedTopLevelFieldsOnSelectionSet &&
                        queryOptions.whiteListedTopLevelFieldsOnSelectionSet
                            ?.length
                    ) {
                        // eslint-disable-next-line no-param-reassign
                        opDefSelection.selectionSet.selections = removeTopLevelFieldsOnSelectionSet(
                            opDefSelection?.selectionSet
                                .selections as SelectionNode[],
                            queryOptions.whiteListedTopLevelFieldsOnSelectionSet,
                        );
                    }
                }
            });

            // all op defs must be of the same type
            otherOpDefs.forEach(_def => {
                if (_def.operation !== def.operation) {
                    throw new Error(
                        `expected all operations to be of the smae type, but ${_def.name?.value} is ${_def.operation} and ${def.name?.value} is ${def.operation}`,
                    );
                }
            });

            // all top level fields mut be unique. doesn't drill down framgents tho. maybe someday
            // eslint-disable-next-line no-unused-expressions
            def.selectionSet.selections
                ?.filter((s): s is FieldNode => s.kind === 'Field')
                .forEach(sel => {
                    otherOpDefs.forEach(_def =>
                        _def.selectionSet.selections
                            ?.filter((s): s is FieldNode => s.kind === 'Field')
                            .forEach(_sel => {
                                if (
                                    (sel.alias?.value || sel.name.value) ===
                                    (_sel.alias?.value || _sel.name.value)
                                ) {
                                    throw new Error(
                                        `duplicate field definition ${_sel.name.value} for oprations ${def.name?.value} and ${_def.name?.value}`,
                                    );
                                }
                            }),
                    );
                });

            // finally all variables must be unique
            // eslint-disable-next-line no-unused-expressions
            def.variableDefinitions?.forEach(variable => {
                otherOpDefs.forEach(_def =>
                    _def.variableDefinitions?.forEach(_variable => {
                        if (
                            variable.variable.name.value ===
                            _variable.variable.name.value
                        ) {
                            throw new Error(
                                `duplicate variable definition ${_variable.variable.name.value} for oprations ${def.name?.value} and ${_def.name?.value}`,
                            );
                        }
                    }),
                );
            });
        });

        let opDefsVariableDefinitions = opDefs.flatMap(
            def => def.variableDefinitions || [],
        );

        if (
            opDefsVariableDefinitions.length > 0 &&
            queryOptions?.clientSideParams &&
            queryOptions?.clientSideParams.length > 0
        ) {
            opDefsVariableDefinitions = removeClientSideParamFromVariableDefinitions(
                opDefsVariableDefinitions,
                queryOptions.clientSideParams,
            );
        }

        const newVars: TVariables & TVariablesAdd = (() => {
            if (this.variables && variables) {
                return {
                    ...this.variables,
                    ...variables,
                } as TVariables & TVariablesAdd;
            }
            return (variables || this.variables) as TVariables & TVariablesAdd;
        })();

        let definitions: DefinitionNode[] = [
            {
                kind: Kind.OPERATION_DEFINITION,
                directives: opDefs.flatMap(def => def.directives || []),
                name: { kind: Kind.NAME, value: this.operationName },
                operation: opDefs[0].operation,
                selectionSet: {
                    kind: Kind.SELECTION_SET,
                    selections: opDefs.flatMap(
                        def => def.selectionSet.selections,
                    ),
                },
                variableDefinitions: opDefsVariableDefinitions,
            },
        ];
        const encounteredFragmentList = new Set<string>();
        const combinedDocumentDefinitions = this.document.definitions.concat(
            document.definitions,
        );

        combinedDocumentDefinitions.forEach(definition => {
            if (definition.kind === 'OperationDefinition') {
                return;
            }
            if (definition.kind === 'FragmentDefinition') {
                if (encounteredFragmentList.has(definition.name.value)) {
                    return;
                }
                encounteredFragmentList.add(definition.name.value);
            }
            definitions = [definition, ...definitions];
        });

        const newDoc: DocumentNode = {
            kind: Kind.DOCUMENT,
            definitions,
        };

        return new CombinedQueryBuilderImpl<
            TData & TDataAdd,
            TVariables & TVariablesAdd
        >(this.operationName, newDoc, newVars);
    }

    addN<TVariablesAdd = OperationVariables>(
        document: DocumentNode,
        variables: TVariablesAdd[],
        queryOptions: QueryOptions,
        variableRenameFn: RenameFnWithIndex = defaultRenameFn,
        fieldRenameFn: RenameFnWithIndex = defaultRenameFn,
    ): CombinedQueryBuilder<TData, TVariables> {
        if (!variables.length) {
            return this;
        }
        return variables.reduce<
            CombinedQueryBuilder<unknown, TVariables & OperationVariables>
        >((builder, _variables, idx): CombinedQueryBuilder<
            unknown,
            TVariables & OperationVariables
        > => {
            const doc = renameVariablesAndTopLevelFields(
                document,
                name => variableRenameFn(name, idx),
                name => fieldRenameFn(name, idx),
            );
            const vars = renameVariables(_variables, name =>
                variableRenameFn(name, idx),
            );
            return builder.add(
                doc,
                vars as any,
                idx < variables.length - 1 ? queryOptions : {},
            );
        }, this);
    }
}

export function combineQuery(operationName: string): NewCombinedQueryBuilder {
    return {
        operationName,
        add<TData = any, TVariables extends OperationVariables = object>(
            document: DocumentNode,
            variables?: TVariables,
        ) {
            return new CombinedQueryBuilderImpl<TData, TVariables>(
                this.operationName,
                document,
                variables,
            );
        },
        /**
         * While using a batched BACH graphql mutation, ensure that we are passing the whole session
         * (session id + gen no) only for the first mutation and only session id (no genNo) for the subsequent ones.
         * Otherwise all the mutations will run on the same genNo which is wrong behavior.
         * Refer useNAddUpdateFilterMutations hook in filters-service.ts
         * @param document
         * @param variables
         * @param queryOptions To improve performance all the fields should only be added in the last query response
         * @param variableRenameFn
         * @param fieldRenameFn
         */
        addN<TVariables = OperationVariables>(
            document: DocumentNode,
            variables: TVariables[],
            queryOptions: QueryOptions,
            variableRenameFn?: RenameFnWithIndex,
            fieldRenameFn?: RenameFnWithIndex,
        ): CombinedQueryBuilder<object, object> {
            return new CombinedQueryBuilderImpl<object, object>(
                this.operationName,
                emptyDoc,
            ).addN<TVariables>(
                document,
                variables,
                queryOptions,
                variableRenameFn,
                fieldRenameFn,
            );
        },
    };
}
